// PRD 14 I5 — Tenis10 Claims, the support page of the Tenis10 migration: the words every part of the page shares (the
// register's states and counters, a child's state, the refusals, the activity log's actions) in one place.
import moment from "moment-timezone"

export const TIMEZONE = "Europe/Bucharest"
export const formatDateTime = value => (value ? moment(value).tz(TIMEZONE).format("D MMM YYYY, HH:mm") : "—")
export const formatDate = value => (value ? moment(value).tz(TIMEZONE).format("D MMM YYYY") : "—")

// The register's filters, in order (the API's `summary.states`)
export const REGISTER_FILTERS = [
  ["all", "All families"],
  ["unclaimed", "With unclaimed children"],
  ["claimed", "Claimed"],
  ["forwarded", "Forwarded 16+"],
  ["hiddenByParent", "Hidden by the parent"],
  ["hiddenBySupport", "Hidden by support"],
  ["hiddenByAccount", "Account deleted"],
  ["bounce", "Bounce"],
  ["unsent", "Not sent yet"],
  ["archived", "Archived"],
]
export const isRegisterFilter = value => REGISTER_FILTERS.some(([key]) => key === value)

// The counters above the register: [label, read from the counters, tone]
export const REGISTER_COUNTERS = [
  ["Families", counters => counters.families.total, "secondary"],
  ["Sent", counters => counters.families.sent, "info"],
  ["Opened", counters => counters.families.opened, "primary"],
  ["Bound to an account", counters => counters.families.bound, "success"],
  ["Bounce", counters => counters.families.bounce, "danger"],
  ["Children unclaimed", counters => counters.children.unclaimed, "secondary"],
  ["Claimed", counters => counters.children.claimed, "success"],
  ["Forwarded 16+", counters => counters.children.forwarded, "warning"],
  ["Hidden by the parent", counters => counters.children.hiddenByParent, "secondary"],
  ["Archived families", counters => counters.families.archived, "secondary"],
]

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

/** A family's children in the register, as badges: [tone, text] each, the empty counts left out. */
export const childrenBadges = children => [
  ["success", children.claimed && plural(children.claimed, "claimed", "claimed")],
  ["secondary", children.unclaimed && `${children.unclaimed} unclaimed`],
  ["warning", children.forwarded && plural(children.forwarded, "forwarded 16+", "forwarded 16+")],
  ["secondary", children.hiddenByParent && `${children.hiddenByParent} hidden by the parent`],
  ["secondary", children.hiddenBySupport && `${children.hiddenBySupport} hidden by support`],
  ["secondary", children.hiddenByAccount && `${children.hiddenByAccount} hidden · account deleted`],
].filter(([, text]) => text)

/** The migration invitation's state in a register row: [tone, text] each. */
export const invitationBadges = row => {
  if (row.archived) return [["secondary", "Archived"]]
  const badges = [row.sentAt ? ["info", `Sent · ${formatDate(row.sentAt)}`] : ["secondary", "Not sent yet"]]
  if (row.bounce) badges.push(["danger", `Bounce · ${row.bounce.kind}`])
  else if (row.openedAt) badges.push(["primary", "Opened"])
  return badges
}

export const HIDDEN_BY = { parent: "by the parent", support: "by support", account: "account deleted" }
export const CLAIM_HOW = { invitation: "family link", support: "support" }

/** A child's state as a badge: [tone, text]. */
export const childBadge = child => {
  if (child.state === "hidden") return ["secondary", `Hidden · ${HIDDEN_BY[child.hidden?.by] || "hidden"}`]
  if (child.state === "claimed") return ["success", `Claimed · ${CLAIM_HOW[child.claim?.how] || child.claim?.how}`]
  // PRD 16 LR12 — a child the parent added in Sportya (an account's page)
  if (child.state === "inAccount") return ["success", "In the account"]
  // … that stays on its deleted account (D4)
  if (child.state === "accountDeleted") return ["secondary", "Account deleted"]
  // PRD 14 I6 — the wave report found the player's forwarded address bounced: support claims the history from here
  if (child.state === "forwarded" && child.forward?.bounce) return ["danger", "Forward bounced"]
  if (child.state === "forwarded") return ["warning", "Forwarded 16+"]
  return ["secondary", "Unclaimed"]
}

export const accountLabel = account => (account ? `#${account.userId}${account.name ? ` · ${account.name}` : ""}` : "—")

// RELEASE-TENIS10 § 11 P78 (Tudor, 2026-10-26: „acolo unde in mod normal afisam ID-ul pentru un adult trebuie sa afisam si pentru
// un copil”): a Tenis10 child's own number (`childId`, from the accounts' counter), shown like an adult's „#75012” wherever an
// adult's #userId shows. `null` while a read carries none: the place then reads as it did before.
export const childNumber = person => {
  const value = person?.childId
  return value === undefined || value === null || value === "" ? null : value
}

// RELEASE-TENIS10 § 11 P103 (Tudor, 2026-10-27: „eticheta T10#… peste tot în CP, un număr simplu caută doar în seriile Sportya,
// id-urile vechi doar cu prefixul T10#”): a tenis10.ro id — the family's tenis10.ro account, a child's tenis10.ro player — is
// written „T10#33245”, never „#33245”, the way a Sportya account's or child's number is written: the two series overlap.
export const legacyIdText = id => (id === undefined || id === null || id === "" ? null : `T10#${id}`)
/** The tenis10.ro id a search writes (`T10#33245`, any case, spaces around the `#`), or null — as the API reads it. */
export const legacyIdSearched = search => {
  const match = /^t10(?:\s*#\s*|\s+)(\d{1,12})$/i.exec(String(search || "").trim())
  return match ? Number(match[1]) : null
}
/** A plain number a search writes (`33245`, `#33245`) — a Sportya account's or child's number only — or null. */
export const plainNumberSearched = search => {
  const match = /^#?(\d{1,12})$/.exec(String(search || "").trim())
  return match ? Number(match[1]) : null
}
export const LEVELS = { rosu: "Roșu", portocaliu: "Portocaliu", verde: "Verde", galben: "Galben" }
export const levelText = child => [LEVELS[child.tenis10Level] || child.tenis10Level, child.adult ? "16+" : child.ageCategory].filter(Boolean).join(" · ") || "—"

// What support may answer a refusal with, in the admin's words
const REFUSALS = {
  SUPPORT_FORBIDDEN: "Only an activated admin can do this.",
  SUPPORT_REQUEST_INVALID: "This request is not valid. Reload the page and try again.",
  SUPPORT_NOTE_REQUIRED: "Write a note of at least 3 characters.",
  SUPPORT_FAMILY_NOT_FOUND: "This family no longer exists.",
  SUPPORT_PROFILE_NOT_FOUND: "This profile no longer exists.",
  SUPPORT_ACCOUNT_NOT_FOUND: "No Sportya account has this ID.",
  SUPPORT_PROFILE_CLAIMED: "This child is claimed. Cancel the claim first.",
  SUPPORT_PROFILE_HIDDEN: "This child is hidden. Show it again first.",
  SUPPORT_PROFILE_NOT_HIDDEN: "This child is not hidden any more.",
  SUPPORT_PROFILE_NOT_MOVABLE: "Only a child claimed under 16 can be moved to another account.",
  SUPPORT_MOVE_SAME_ACCOUNT: "The child is already in this account.",
  SUPPORT_FAMILY_NOT_SENT: "The family's first email leaves with its wave. Nothing can be resent before.",
  SUPPORT_FAMILY_NOT_MAILABLE: "The family's tenis10.ro address cannot be mailed. Send the link to a new email the parent gives you, or claim the children from here.",
  SUPPORT_FAMILY_BOUNCED: "The family's address bounced. Send the link to a new email the parent gives you, or claim the children from here.",
  SUPPORT_FAMILY_ARCHIVED: "Every child of this family is claimed or hidden: there is nothing to resend.",
  SUPPORT_RESEND_FAILED: "The email could not be sent. The family keeps its previous link. Try again later.",
  UNCLAIMED_PROFILE_ALREADY_CLAIMED: "Another account claimed this child meanwhile. Reload the family.",
  UNCLAIMED_PROFILE_HIDDEN: "This child is hidden. Show it again first.",
  UNCLAIMED_PROFILE_NOT_CLAIMED: "This child is not claimed any more. Reload the family.",
  UNCLAIMED_PROFILE_CLAIM_BUSY: "The family is changing this child right now. Try again in a moment.",
  CLAIM_ACCOUNT_NOT_ALLOWED: "This account cannot receive the child: it must be an activated player account.",
  // PRD 16 LR12 — the passport support activates
  SUPPORT_PROFILE_NOT_MANAGED: "Only a child a Sportya account holds can get a passport. Claim the child first.",
  SUPPORT_PASSPORT_HELD: "This child holds a passport already (bought, with an entry or activated). Reload the page.",
  SUPPORT_PASSPORT_DAY_INVALID: "Choose today, an earlier day of this year, or 1 January of next year.",
  // RELEASE-TENIS10 § 11 P5 — support's correction of a child's data
  SUPPORT_CHILD_DATA_UNCHANGED: "Nothing changed: the child already has this data. Reload the page.",
  SUPPORT_CHILD_AGE_INVALID: "A Tenis10 child is 4 to 15 by 31 December: choose a birth date in those years.",
  SUPPORT_CHILD_LEVEL_NOT_ELIGIBLE: "This level does not suit the child's age: its own level or a higher one only.",
  // RELEASE-TENIS10 § 11 P55 — the family link sent to a new address
  SUPPORT_EMAIL_INVALID: "Enter a valid email address.",
  SUPPORT_EMAIL_SAME: "The family's emails already use this address. Write the new address the parent gave you.",
  SUPPORT_EMAIL_BOUNCED: "This address rejected an email before and is never mailed again. Ask the parent for another address.",
  SUPPORT_FAMILY_CHANGED: "Another admin changed the family's address meanwhile. Reload the page.",
  SESSION_EXPIRED: "Your session expired. Sign in again.",
}
export const refusalFor = code => REFUSALS[code] || null

/** The text of a failed request: the refusal's own words (a 4xx, or a 5xx the API names — the resend that could not leave), or the screen's fallback. */
export const errorText = (error, fallback) => {
  const status = error?.response?.status
  return (status >= 400 && refusalFor(error.response.data?.message)) || fallback
}

// The activity log's actions, in the admin's words (an action the API adds later shows as it is)
export const LOG_ACTIONS = {
  "unclaimed-profile-claimed": entry => (entry.details?.how === "support" ? "Tenis10 claim (support)" : "Tenis10 claim (family link)"),
  "unclaimed-profile-claim-cancelled": () => "Tenis10 claim cancelled",
  "unclaimed-profile-moved": () => "Tenis10 child moved",
  "unclaimed-profile-hidden": () => "Tenis10 profile hidden (by support)",
  "unclaimed-profile-shown": () => "Tenis10 profile shown again",
  "migration-child-hidden": () => "Tenis10 profile hidden (by the parent)",
  "migration-invitation-sent": () => "Migration invitation sent",
  "migration-invitation-resent": () => "Family link resent (support)",
  "migration-invitation-sent-to-email": () => "Family link sent to a new email (support)",
  "migration-link-requested": () => "New link asked for („Cere din nou”)",
  "migration-child-forwarded": () => "16+ child forwarded to the player",
  "migration-account-created": () => "Account made from the invitation",
  "migration-reminder-sent": () => "Reminder sent",
  "tenis10-passport-activated": () => "Tenis10 passport activated (support)",
  "tenis10-child-data-corrected": () => "Tenis10 child data corrected (support)",
}

// RELEASE-TENIS10 § 11 P5 — the fields a correction of a child's data changed, as the log names them
export const CHILD_DATA_FIELDS = {
  firstName: "first name", lastName: "last name", gender: "gender", birthDate: "date of birth", location: "city", tenis10Level: "level",
}
export const fieldsText = fields => (Array.isArray(fields) && fields.length ? fields.map(field => CHILD_DATA_FIELDS[field] || field).join(", ") : null)
export const logActionText = entry => (LOG_ACTIONS[entry.action] ? LOG_ACTIONS[entry.action](entry) : entry.action || "—")

// who did it: the admin, a signed-in account, the family through its link, or the waves (the send and the reminders)
const SYSTEM_ACTIONS = ["migration-invitation-sent", "migration-reminder-sent", "migration-link-requested"]
export const actorText = (actor, action) => {
  if (!actor?.userId) return SYSTEM_ACTIONS.includes(action) ? "Sportya (waves)" : "Family link"
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(" ")
  return name ? `${name} (#${actor.userId})` : `#${actor.userId}`
}
